import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import logo from '../user/logo.png';

/**
 * Единый сайдбар для /admin и /dashboard.
 *
 * Компонент «глупый»: стиль, раскрытие разделов и компактность живут здесь,
 * а состав меню приходит снаружи в виде конфигурации `sections`.
 *  - пункты описывают права (`perms`), фильтрация идёт через переданный `hasPerm` (PBAC) - и в /admin, и в /dashboard;
 *  - `hidden` - для пунктов, скрытых не по правам.
 *
 * Профиль пользователя в сайдбаре не показывается - он вынесен в шапку (AppHeader).
 *
 * ВАЖНО: любой новый пункт меню нужно также добавлять в каталоги стартовых страниц
 * (frontend/src/config/startPages.ts и backend/services/startPages.js).
 */

export interface SidebarItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  /** Индекс страницы в AdminPanel / Dashboard */
  index: number;
  /** Подстраница (например, модуль «Отчетов») */
  module?: string;
  /** Все перечисленные права обязательны для показа пункта (проверяется через `hasPerm`) */
  perms?: string[];
  /** Явное скрытие пункта на уровне кода */
  hidden?: boolean;
}

export interface SidebarSection {
  key: string;
  /** Без заголовка - раздел всегда раскрыт и не сворачивается (например, «Главная») */
  title?: string;
  items: SidebarItem[];
  hidden?: boolean;
}

export interface SidebarFooterAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
  danger?: boolean;
  /** Только иконка (с подсказкой): такие кнопки выстраиваются справа в одной строке с основной */
  iconOnly?: boolean;
}

interface AppSidebarProps {
  /** Идентификатор интерфейса: используется для хранения состояния разделов */
  id: string;
  sections: SidebarSection[];
  selectedIndex: number;
  selectedModule?: string;
  onSelect: (index: number, module?: string) => void;
  /** Если задан - работает средняя кнопка мыши и контекстное меню «Открыть в новой вкладке» */
  onOpenInNewTab?: (index: number, module?: string) => void;
  /** Проверка права; если не передана, `perms` у пунктов игнорируются */
  hasPerm?: (code: string) => boolean;
  /** Блокировка всех пунктов меню (например, пока не заполнен профиль) */
  disabled?: boolean;
  footerActions?: SidebarFooterAction[];
}

const SIDEBAR_WIDTH = 232;
// Палитра прежнего тёмного сайдбара кабинета аптеки
const BG = '#000000';
const LINE = 'rgba(255, 255, 255, 0.1)';
const TEXT = '#a1a1aa';
const TEXT_ACTIVE = '#ffffff';

const itemSx = {
  position: 'relative',
  minHeight: 32,
  py: 0.5,
  pl: 1.5,
  pr: 1,
  mx: 0.75,
  mb: '1px',
  borderRadius: '6px',
  color: TEXT,
  transition: 'background-color 0.12s ease, color 0.12s ease',
  '& .MuiListItemIcon-root': { minWidth: 28, color: 'inherit', transition: 'color 0.12s ease' },
  '& .MuiListItemIcon-root svg': { fontSize: 18 },
  '& .MuiListItemText-primary': { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.3 },
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: TEXT_ACTIVE },
  '&.Mui-selected': {
    bgcolor: 'rgba(255, 255, 255, 0.1)',
    color: TEXT_ACTIVE,
    '& .MuiListItemText-primary': { fontWeight: 600 },
    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.14)' }
  },
  '&.Mui-disabled': { opacity: 0.45 }
} as const;

const loadOpenSections = (id: string): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(`sidebarSections:${id}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const AppSidebar: React.FC<AppSidebarProps> = ({
  id,
  sections,
  selectedIndex,
  selectedModule,
  onSelect,
  onOpenInNewTab,
  hasPerm,
  disabled = false,
  footerActions = []
}) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => loadOpenSections(id));
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: SidebarItem } | null>(null);

  // Фильтрация по правам (PBAC) и явному hidden; пустые разделы не показываются
  const visibleSections = useMemo(() => {
    const isVisible = (item: SidebarItem) => {
      if (item.hidden) return false;
      if (hasPerm && item.perms && !item.perms.every(hasPerm)) return false;
      return true;
    };
    return sections
      .filter(s => !s.hidden)
      .map(s => ({ ...s, items: s.items.filter(isVisible) }))
      .filter(s => s.items.length > 0);
  }, [sections, hasPerm]);

  const isActive = (item: SidebarItem) =>
    item.index === selectedIndex && item.module === selectedModule;

  const activeSectionKey = visibleSections.find(s => s.items.some(isActive))?.key;

  // Раздел с активной страницей всегда раскрывается (переход по ссылке, стартовая страница)
  useEffect(() => {
    if (!activeSectionKey) return;
    setOpenSections(prev => (prev[activeSectionKey] ? prev : { ...prev, [activeSectionKey]: true }));
  }, [activeSectionKey]);

  useEffect(() => {
    try {
      localStorage.setItem(`sidebarSections:${id}`, JSON.stringify(openSections));
    } catch {
      // хранилище недоступно - состояние просто не сохраняется
    }
  }, [id, openSections]);

  const toggleSection = (key: string) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const renderItem = (item: SidebarItem) => (
    <ListItemButton
      key={item.key}
      selected={isActive(item)}
      disabled={disabled}
      onClick={() => onSelect(item.index, item.module)}
      onAuxClick={onOpenInNewTab ? (e) => { if (e.button === 1) onOpenInNewTab(item.index, item.module); } : undefined}
      onContextMenu={onOpenInNewTab ? (e) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX - 2, y: e.clientY - 4, item });
      } : undefined}
      sx={itemSx}
    >
      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
      <ListItemText primary={item.label} />
    </ListItemButton>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BG,
        borderRight: `1px solid ${LINE}`
      }}
    >
      <Box sx={{ flexShrink: 0, overflow: 'hidden' }}>
        <img
          src={logo}
          alt="Zerde"
          style={{ display: 'block', width: '100%', height: 'auto', padding: 16, boxSizing: 'border-box' }}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', pb: 1, mt: 0.5 }}>
        {visibleSections.map(section => {
          if (!section.title) {
            return <List key={section.key} disablePadding sx={{ pt: 0.5 }}>{section.items.map(renderItem)}</List>;
          }
          const open = Boolean(openSections[section.key]);
          return (
            <Box key={section.key} sx={{ mt: 0.5 }}>
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => toggleSection(section.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(section.key); } }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 0.75,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: section.key === activeSectionKey ? TEXT_ACTIVE : '#71717a',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  transition: 'color 0.12s ease',
                  '&:hover': { color: TEXT_ACTIVE },
                  '&:focus-visible': { outline: `2px solid ${TEXT_ACTIVE}`, outlineOffset: -2 }
                }}
              >
                <span>{section.title}</span>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    transform: open ? 'none' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease'
                  }}
                />
              </Box>
              <Collapse in={open} timeout={150}>
                <List disablePadding>{section.items.map(renderItem)}</List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {footerActions.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.75,
            borderTop: `1px solid ${LINE}`
          }}
        >
          {footerActions.filter(a => !a.iconOnly).map(action => (
            <ListItemButton
              key={action.key}
              selected={action.selected}
              onClick={action.onClick}
              sx={{
                ...itemSx,
                mx: 0,
                mb: 0,
                flexGrow: 1,
                ...(action.danger
                  ? {
                    color: '#f87171',
                    '&:hover': { bgcolor: 'rgba(248, 113, 113, 0.12)', color: '#fca5a5' }
                  }
                  : {})
              }}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText primary={action.label} />
            </ListItemButton>
          ))}
          {footerActions.filter(a => a.iconOnly).map(action => (
            <Tooltip key={action.key} title={action.label} placement="top" arrow>
              <IconButton
                size="small"
                aria-label={action.label}
                onClick={action.onClick}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  color: action.selected ? TEXT_ACTIVE : TEXT,
                  bgcolor: action.selected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  '& svg': { fontSize: 19 },
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)', color: TEXT_ACTIVE }
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      )}

      {onOpenInNewTab && (
        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
        >
          <MenuItem
            dense
            onClick={() => {
              if (contextMenu) onOpenInNewTab(contextMenu.item.index, contextMenu.item.module);
              setContextMenu(null);
            }}
          >
            Открыть в новой вкладке
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
};

export default AppSidebar;
